// components/PlanPane.tsx — K5's own pane (c6-plan-authorization.md:
// "Engagement spread -> Plan pane" — no new screen). One engagement may
// carry several plans; each plan's own actions (Hold / Approve / the five
// outs / Override) render inline under its row, one expanded at a time.
//
// Machine voice; a refusal shows the SERVER's own sentence plus the five
// outs as buttons straight off the refusal payload — no caption of ours
// explaining what they mean.
import { useEffect, useState } from 'preact/hooks';
import { routePath } from '../routes/route.js';
import {
  ApiError,
  PlanRefusedError,
  listPlans,
  createPlan,
  updatePlan,
  holdPlan as apiHoldPlan,
  approvePlan as apiApprovePlan,
  overridePlan as apiOverridePlan,
  displacePlanHold,
  getPlanConsequence,
  listPlanForecasts,
  revisePlan as apiRevisePlan,
  acceptRevision as apiAcceptRevision,
  markPlanInternal,
  type RecordRow,
  type PlanOutApi,
  type PlanConsequenceApi,
  type PlanForecastApi,
} from '../lib/api';

export interface PlanPaneProps {
  tenantId: string;
  engagementId: string;
}

const STATUS_WORD: Record<string, string> = {
  proposed: 'proposed', held: 'held', approved: 'approved', overridden: 'overridden', withdrawn: 'withdrawn',
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

interface Refusal {
  message: string;
  outs: PlanOutApi[];
}

export function PlanPane({ tenantId, engagementId }: PlanPaneProps) {
  const [plans, setPlans] = useState<RecordRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [scope, setScope] = useState('');
  const [proposedStart, setProposedStart] = useState(today());
  const [proposedEnd, setProposedEnd] = useState(today());
  const [estimatedHours, setEstimatedHours] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('');

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [effectiveOn, setEffectiveOn] = useState(today());
  const [overrideReason, setOverrideReason] = useState('');
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [displacing, setDisplacing] = useState(false);
  const [overriding, setOverriding] = useState(false);
  const [revising, setRevising] = useState(false);

  // V15 (v15-capacity-and-governing-agreement.md) — the consequence card
  // (committed/reserved/tentative per week + the verdict word) for the
  // plan currently being approved, and the forecast history for every
  // approved/overridden plan (the three-date rows).
  const [consequence, setConsequence] = useState<PlanConsequenceApi | null>(null);
  const [forecastsByPlan, setForecastsByPlan] = useState<Record<string, PlanForecastApi[]>>({});
  const [askingRevision, setAskingRevision] = useState<string | null>(null);
  const [revisionStart, setRevisionStart] = useState(today());
  const [revisionEnd, setRevisionEnd] = useState(today());
  const [revisionReason, setRevisionReason] = useState('');

  function refresh() {
    return listPlans(tenantId, engagementId)
      .then((rows) => {
        setPlans(rows);
        // V15 — the forecast history (the "current forecast" row) is
        // fetched only for plans that HAVE a promise to compare against;
        // a proposed/held plan has no promised/forecast dates yet.
        const dated = rows.filter((p) => p.lifecycle_state === 'approved' || p.lifecycle_state === 'overridden');
        void Promise.all(
          dated.map((p) => listPlanForecasts(tenantId, p.id).then((fs) => [p.id, fs] as const))
        ).then((pairs) => setForecastsByPlan(Object.fromEntries(pairs)));
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'plans could not be read'));
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, engagementId]);

  function resetActionState() {
    setRefusal(null);
    setDisplacing(false);
    setOverriding(false);
    setRevising(false);
    setOverrideReason('');
  }

  async function create() {
    if (!title.trim()) {
      setError('a plan needs a title');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await createPlan(tenantId, {
        engagement_id: engagementId,
        title: title.trim(),
        scope: scope.trim() || null,
        proposed_start: proposedStart,
        proposed_end: proposedEnd,
        estimated_hours: estimatedHours ? Number(estimatedHours) : null,
        price: price ? Number(price) : null,
        currency: currency.trim() || null,
      });
      setCreating(false);
      setTitle('');
      setScope('');
      setEstimatedHours('');
      setPrice('');
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'that plan was refused');
    } finally {
      setBusy(false);
    }
  }

  async function hold(plan: RecordRow) {
    setBusy(true);
    setError(null);
    try {
      await apiHoldPlan(tenantId, plan.id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'that hold was refused');
    } finally {
      setBusy(false);
    }
  }

  async function approve(plan: RecordRow) {
    setBusy(true);
    resetActionState();
    try {
      await apiApprovePlan(tenantId, plan.id, effectiveOn);
      setExpandedId(null);
      await refresh();
    } catch (err) {
      if (err instanceof PlanRefusedError) {
        setRefusal({ message: err.message, outs: err.outs });
      } else {
        setError(err instanceof ApiError ? err.message : 'that approval was refused');
      }
    } finally {
      setBusy(false);
    }
  }

  async function override(plan: RecordRow) {
    if (!overrideReason.trim()) return;
    setBusy(true);
    try {
      await apiOverridePlan(tenantId, plan.id, overrideReason.trim());
      setExpandedId(null);
      resetActionState();
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'that override was refused');
    } finally {
      setBusy(false);
    }
  }

  async function displace(plan: RecordRow, otherId: string) {
    setBusy(true);
    try {
      await displacePlanHold(tenantId, plan.id, otherId);
      setDisplacing(false);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'that displacement was refused');
    } finally {
      setBusy(false);
    }
  }

  async function revise(plan: RecordRow, fields: Record<string, unknown>) {
    setBusy(true);
    try {
      await updatePlan(tenantId, plan.id, fields);
      setRevising(false);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'that revision was refused');
    } finally {
      setBusy(false);
    }
  }

  function outAction(plan: RecordRow, outId: string) {
    if (outId === 'displace_hold') { setDisplacing(true); return; }
    if (outId === 'override') { setOverriding(true); return; }
    if (outId === 'revise_scope' || outId === 'move_date' || outId === 'change_price') { setRevising(true); return; }
  }

  return (
    <>
      <div class="sp__sec" style={{ marginTop: '16px' }}><span>Plan</span></div>
      {plans.length === 0 ? (
        <div class="sp__empty">No plan yet — a plan carries proposed dates, hours and price for this engagement.</div>
      ) : (
        plans.map((p) => {
          const status = String(p.lifecycle_state ?? '');
          const otherHeld = plans.filter((o) => o.id !== p.id && o.lifecycle_state === 'held');
          return (
            <div key={p.id} class="sp__row" style={{ display: 'block', cursor: 'default' }}>
              <span class="sp__row-top">
                <span class="sp__row-name">
                  <span class="sp__chip">{STATUS_WORD[status] ?? status}</span>
                  {p.fields.internal ? <span class="sp__chip" data-testid="plan-internal-chip">internal</span> : null}
                  {' '}{String(p.fields.title ?? p.display_name)}
                </span>
                <span class="sp__row-when">
                  {String(p.fields.proposed_start ?? '')}
                  {p.fields.proposed_end ? ` – ${String(p.fields.proposed_end)}` : ''}
                </span>
              </span>
              <span class="sp__row-sub">
                {String(p.fields.estimated_hours ?? '—')}h
                {p.fields.price ? ` · ${String(p.fields.price)} ${String(p.fields.currency ?? '')}` : ''}
              </span>

              {/* V15 (DATES-01) — the three date rows: promised (frozen),
                  the current forecast (history — latest row), and the
                  proposed revision (an ask). Never the same row rendered
                  twice — each carries its own label. */}
              {status === 'approved' || status === 'overridden' ? (
                <div class="sp__dates" data-testid="plan-three-dates">
                  <div class="sp__row-sub" data-testid="plan-date-promised">
                    Promised: {String(p.fields.promised_start ?? '—')} – {String(p.fields.promised_end ?? '—')}
                  </div>
                  {(() => {
                    const latestForecast = forecastsByPlan[p.id]?.[0];
                    return latestForecast ? (
                      <div class="sp__row-sub" data-testid="plan-date-forecast">
                        Forecast: {latestForecast.forecastStart} – {latestForecast.forecastEnd}
                      </div>
                    ) : null;
                  })()}
                  {p.fields.revision_status === 'proposed' ? (
                    <div class="sp__row-sub" data-testid="plan-date-revision">
                      Proposed revision: {String(p.fields.revision_proposed_start ?? '—')} – {String(p.fields.revision_proposed_end ?? '—')}
                    </div>
                  ) : null}
                  <div class="sp__acts">
                    <button
                      type="button"
                      class="sp__act"
                      disabled={busy}
                      onClick={() => { setAskingRevision(p.id); setRevisionStart(today()); setRevisionEnd(today()); setRevisionReason(''); }}
                    >
                      Ask a revision
                    </button>
                    {p.fields.revision_status === 'proposed' ? (
                      <button
                        type="button"
                        class="sp__act"
                        disabled={busy}
                        onClick={() => {
                          setBusy(true);
                          apiAcceptRevision(tenantId, p.id, today())
                            .then(() => refresh())
                            .catch((err: unknown) => {
                              if (err instanceof PlanRefusedError) setRefusal({ message: err.message, outs: err.outs });
                              else setError(err instanceof ApiError ? err.message : 'that revision could not be accepted');
                            })
                            .finally(() => setBusy(false));
                        }}
                      >
                        Accept the revision
                      </button>
                    ) : null}
                  </div>
                  {askingRevision === p.id ? (
                    <div class="sp__acts" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                      <input class="sp__act" type="date" value={revisionStart} disabled={busy} onInput={(e) => setRevisionStart((e.target as HTMLInputElement).value)} />
                      <input class="sp__act" type="date" value={revisionEnd} disabled={busy} onInput={(e) => setRevisionEnd((e.target as HTMLInputElement).value)} />
                      <input class="sp__act" type="text" placeholder="Reason" value={revisionReason} disabled={busy} onInput={(e) => setRevisionReason((e.target as HTMLInputElement).value)} />
                      <button
                        type="button"
                        class="sp__act"
                        disabled={busy || !revisionReason.trim()}
                        onClick={() => {
                          setBusy(true);
                          apiRevisePlan(tenantId, p.id, { proposedStart: revisionStart, proposedEnd: revisionEnd, reason: revisionReason.trim() })
                            .then(() => { setAskingRevision(null); return refresh(); })
                            .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'that revision could not be proposed'))
                            .finally(() => setBusy(false));
                        }}
                      >
                        Save the revision ask
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {status === 'proposed' || status === 'held' ? (
                <div class="sp__acts">
                  {status === 'proposed' ? (
                    <button type="button" class="sp__act" disabled={busy} onClick={() => void hold(p)}>Hold the dates</button>
                  ) : null}
                  {/* V15 (INV-CAP-4) — internal/external is settled BEFORE
                      approval; approvePlan's own governing gate reads it. */}
                  <button
                    type="button"
                    class="sp__act"
                    disabled={busy}
                    data-testid="plan-mark-internal"
                    onClick={() => {
                      setBusy(true);
                      markPlanInternal(tenantId, p.id, !p.fields.internal)
                        .then(() => refresh())
                        .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'that could not be set'))
                        .finally(() => setBusy(false));
                    }}
                  >
                    {p.fields.internal ? 'Mark external (a client promise)' : 'Mark internal (no client promise)'}
                  </button>
                </div>
              ) : null}

              {status === 'held' ? (
                expandedId !== p.id ? (
                  <div class="sp__acts">
                    <button
                      type="button"
                      class="sp__act"
                      onClick={() => {
                        setExpandedId(p.id);
                        resetActionState();
                        setConsequence(null);
                        void getPlanConsequence(tenantId, p.id).then(setConsequence).catch(() => setConsequence(null));
                      }}
                    >
                      Approve
                    </button>
                  </div>
                ) : (
                  <div class="sp__acts" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                    {/* V15 — the consequence card: committed · reserved ·
                        tentative per week, and the verdict word. */}
                    {consequence ? (
                      <div class="sp__row-sub" data-testid="plan-consequence-card">
                        <div data-testid="plan-consequence-verdict">Verdict: {consequence.verdict}</div>
                        {consequence.weekly.map((w) => (
                          <div key={w.weekStart}>
                            week of {w.weekStart}: committed {w.committedHours}h · reserved {w.reservedHours}h · tentative {w.tentativeHours}h
                            {w.ceiling !== null ? ` (ceiling ${w.ceiling}h)` : ''}
                          </div>
                        ))}
                        {consequence.beyondLookahead.length > 0 ? (
                          <div>beyond lookahead: {consequence.beyondLookahead.join(', ')}</div>
                        ) : null}
                      </div>
                    ) : null}
                    <input class="sp__act" type="date" value={effectiveOn} disabled={busy} onInput={(e) => setEffectiveOn((e.target as HTMLInputElement).value)} />
                    <button type="button" class="sp__act" disabled={busy} onClick={() => void approve(p)}>Approve</button>
                    <button type="button" class="sp__act" disabled={busy} onClick={() => { setExpandedId(null); resetActionState(); }}>Never mind</button>

                    {refusal ? (
                      <>
                        <p class="sp__age sp__age--late">{refusal.message}</p>
                        <div class="sp__acts">
                          {refusal.outs.map((o) =>
                            o.id === 'record_missing_number' ? (
                              // V15 review (2026-09-16): the sixth out leads somewhere — the missing number is a
                              // lever or the plan policy, both on Settings; a control that does nothing is a
                              // silent failure on the screen.
                              <a class="sp__act" key={o.id} href={routePath({ screen: 'settings' })} data-testid="out-record-missing-number">{o.label}</a>
                            ) : (
                              <button type="button" class="sp__act" key={o.id} disabled={busy} onClick={() => outAction(p, o.id)}>{o.label}</button>
                            )
                          )}
                        </div>
                      </>
                    ) : null}

                    {revising ? (
                      <div class="sp__acts" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                        <input class="sp__act" type="text" placeholder="Scope" onInput={(e) => (p.fields.scope = (e.target as HTMLInputElement).value)} />
                        <input class="sp__act" type="date" placeholder="Proposed start" onInput={(e) => (p.fields.proposed_start = (e.target as HTMLInputElement).value)} />
                        <input class="sp__act" type="date" placeholder="Proposed end" onInput={(e) => (p.fields.proposed_end = (e.target as HTMLInputElement).value)} />
                        <input class="sp__act" type="number" placeholder="Price" onInput={(e) => (p.fields.price = Number((e.target as HTMLInputElement).value))} />
                        <button
                          type="button"
                          class="sp__act"
                          disabled={busy}
                          onClick={() =>
                            void revise(p, {
                              scope: p.fields.scope,
                              proposed_start: p.fields.proposed_start,
                              proposed_end: p.fields.proposed_end,
                              price: p.fields.price,
                            })
                          }
                        >
                          Save the revision
                        </button>
                      </div>
                    ) : null}

                    {displacing ? (
                      <div class="sp__acts" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                        {otherHeld.length === 0 ? (
                          <div class="sp__empty">No other held plan to displace.</div>
                        ) : (
                          otherHeld.map((o) => (
                            <button type="button" class="sp__act" key={o.id} disabled={busy} onClick={() => void displace(p, o.id)}>
                              Displace {String(o.fields.title ?? o.display_name)}
                            </button>
                          ))
                        )}
                      </div>
                    ) : null}

                    {overriding ? (
                      <div class="sp__acts" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                        <input
                          class="sp__act"
                          type="text"
                          placeholder="Reason"
                          value={overrideReason}
                          disabled={busy}
                          onInput={(e) => setOverrideReason((e.target as HTMLInputElement).value)}
                        />
                        <button type="button" class="sp__act" disabled={busy} onClick={() => void override(p)}>Override</button>
                      </div>
                    ) : null}
                  </div>
                )
              ) : null}
            </div>
          );
        })
      )}

      {!creating ? (
        <div class="sp__acts">
          <button type="button" class="sp__act" onClick={() => setCreating(true)}>New plan</button>
        </div>
      ) : (
        <div class="sp__acts" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
          <input class="sp__act" type="text" placeholder="Title" value={title} disabled={busy} onInput={(e) => setTitle((e.target as HTMLInputElement).value)} />
          <input class="sp__act" type="text" placeholder="Scope (optional)" value={scope} disabled={busy} onInput={(e) => setScope((e.target as HTMLInputElement).value)} />
          <input class="sp__act" type="date" value={proposedStart} disabled={busy} onInput={(e) => setProposedStart((e.target as HTMLInputElement).value)} />
          <input class="sp__act" type="date" value={proposedEnd} disabled={busy} onInput={(e) => setProposedEnd((e.target as HTMLInputElement).value)} />
          <input class="sp__act" type="number" placeholder="Estimated hours" value={estimatedHours} disabled={busy} onInput={(e) => setEstimatedHours((e.target as HTMLInputElement).value)} />
          <input class="sp__act" type="number" placeholder="Price (blank for design partner)" value={price} disabled={busy} onInput={(e) => setPrice((e.target as HTMLInputElement).value)} />
          <input class="sp__act" type="text" placeholder="Currency" value={currency} disabled={busy} onInput={(e) => setCurrency((e.target as HTMLInputElement).value)} />
          <button type="button" class="sp__act" disabled={busy} onClick={() => void create()}>Save</button>
          <button type="button" class="sp__act" disabled={busy} onClick={() => setCreating(false)}>Never mind</button>
        </div>
      )}
      {error ? <p class="sp__age sp__age--late">{error}</p> : null}
    </>
  );
}
